import React, { useState } from 'react';
import SettingCard from './SettingCard';
import { panelLabel } from '../../utils/panelLabels';
import { useSettingsReadOnly } from './readOnlyContext';

// Colours taken from the panel's own stylesheet so the two screens read the
// same on a support call: the discharge orange fills the rate readouts and the
// selected tab, local mode is blue, manual mode is orange.
const DISCHARGE = '#f0ad4e';
const LOCAL = '#2196f3';
const MANUAL = '#ff9800';
const OTHER = '#9e9e9e';

const DischargeRateSetup = ({ getSettingValue, handleEdit, handleModeChange }) => {
  const readOnly = useSettingsReadOnly();
  const [activeTab, setActiveTab] = useState(0);

  const modeNum = Number(getSettingValue('dryer_master_mode').value);
  const isLocal = modeNum === 0;
  const isManual = modeNum === 1;
  const modeColor = isLocal ? LOCAL : isManual ? MANUAL : OTHER;
  const modeLabel = { 0: 'Local', 1: 'Manual', 2: 'Automatic' }[modeNum] ?? '--';

  // The panel names its control limits off the home feed, so the labels come
  // from those keys rather than the analog-output names the cloud uses.
  const controlLimits = [
    { name: 'ao_discharge_rate_sp_aomax', labelKey: 'maximum_discharge_rate_in_auto', fallback: 'Maximum Rate' },
    { name: 'ao_discharge_rate_sp_aomin', labelKey: 'minimum_discharge_rate_in_auto', fallback: 'Minimum Rate' },
    { name: 'dryer_discharge_off_rate', fallback: 'Dryer Off Rate' }
  ];

  const settingCard = (name, labelKey, fallback) => {
    const setting = getSettingValue(name);
    return (
      <SettingCard
        key={name}
        name={panelLabel(labelKey || name, fallback)}
        value={setting.value}
        min={setting.minRange}
        max={setting.maxRange}
        onEdit={(_, value) => handleEdit(name, value)}
      />
    );
  };

  // Rate readouts: solid discharge orange with white text, as on the panel.
  const rateBadge = (name, labelKey, fallback) => {
    const raw = getSettingValue(name).value;
    return (
      <div
        key={name}
        className="rounded-lg border-2 p-4 shadow-sm flex flex-col items-center justify-center text-center"
        style={{ borderColor: DISCHARGE, backgroundColor: DISCHARGE }}
      >
        <p className="text-4xl font-bold text-white leading-none mb-2">
          {raw !== null && raw !== undefined ? parseFloat(raw).toFixed(2) : '--'}
        </p>
        <p className="text-sm font-semibold text-white uppercase tracking-wide opacity-90">
          {panelLabel(labelKey, fallback)}
        </p>
      </div>
    );
  };

  // One card for both tabs. The input side only offers the switch to local;
  // the output side offers local and manual, as the panel does.
  const modeCard = (showManual) => (
    <div
      className="rounded-lg border-2 shadow-sm overflow-hidden flex flex-col"
      style={{ borderColor: modeColor, backgroundColor: `${modeColor}14` }}
    >
      <div className="flex-1 px-4 py-3 text-center">
        <p className="text-gray-500 text-sm font-semibold uppercase tracking-wide">
          {panelLabel('control_mode', 'Control Mode')}
        </p>
        <p className="text-2xl font-bold uppercase tracking-wide mt-1" style={{ color: modeColor }}>
          {modeLabel}
        </p>
      </div>
      {!readOnly && (
        <div className={`grid ${showManual ? 'grid-cols-2' : 'grid-cols-1'} gap-2 p-3`}>
          <button
            onClick={() => !isLocal && handleModeChange('local_mode')}
            disabled={isLocal}
            className={`py-2.5 rounded-md font-semibold text-sm text-white transition-opacity ${
              isLocal ? 'opacity-50 cursor-default' : 'hover:opacity-90'
            }`}
            style={{ backgroundColor: LOCAL }}
          >
            Local
          </button>
          {/* No Automatic here, as on the panel. Going into automatic needs the
              dryer running and the model ready, and the home dashboard's mode
              selector is the place that checks. */}
          {showManual && (
            <button
              onClick={() => !isManual && handleModeChange('manual_mode')}
              disabled={isManual}
              className={`py-2.5 rounded-md font-semibold text-sm text-white transition-opacity ${
                isManual ? 'opacity-50 cursor-default' : 'hover:opacity-90'
              }`}
              style={{ backgroundColor: MANUAL }}
            >
              Manual
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Input / Output tabs — the one you are on wears the discharge orange */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {['Input', 'Output'].map((title, idx) => (
          <button
            key={title}
            onClick={() => setActiveTab(idx)}
            className={`py-4 rounded-lg font-bold text-xl border shadow-sm transition-colors ${
              activeTab === idx
                ? 'text-white border-transparent'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            style={activeTab === idx ? { backgroundColor: DISCHARGE } : {}}
          >
            {title}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 0 ? (
          /* ===== INPUT TAB — slope | offset over discharge rate | mode ===== */
          <div className="grid grid-cols-2 gap-4 auto-rows-fr mb-6">
            {settingCard('rate_input_slope')}
            {settingCard('rate_input_offset')}
            {rateBadge('rate_input', 'discharge_rate', 'Discharge rate')}
            {modeCard(false)}
          </div>
        ) : (
          /* ===== OUTPUT TAB ===== */
          <>
            <div className="grid grid-cols-2 gap-4 auto-rows-fr mb-6">
              {settingCard('rate_output_slope')}
              {settingCard('rate_output_offset')}
              {settingCard('discharge_rate_setpoint')}
              {modeCard(true)}
            </div>

            <div className="grid grid-cols-2 gap-4 auto-rows-fr mb-6">
              {rateBadge('rate_input', 'discharge_rate', 'Discharge rate')}
              {rateBadge('rate_output', 'rate_output', 'Rate output')}
            </div>

            <div className="grid grid-cols-2 gap-4 auto-rows-fr">
              {controlLimits.map((limit) => settingCard(limit.name, limit.labelKey, limit.fallback))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DischargeRateSetup;
